import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CircularProgress, List, ListItem, FormControlLabel, Checkbox, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DataLoader from '../../components/data-loader';

interface InfiniteScrollListProps<T> {
    items: T[];
    hasMore: boolean;
    fetchMoreData: () => void;
    itemIdentifier: (item: T) => string;
    renderLabel: (item: T) => React.ReactNode;
    selectedItems: T[];
    handleItemChange: (event: React.ChangeEvent<HTMLInputElement>, item: T) => void;
    scrollableTarget?: string;
    scrollThreshold?: number;
    style?: React.CSSProperties;
    emptyStateMessage?: string;
    isLoading?: boolean;
}

const InfiniteScrollList = <T,>({
    items,
    hasMore,
    fetchMoreData,
    itemIdentifier,
    renderLabel,
    selectedItems,
    handleItemChange,
    scrollableTarget = 'scrollableDiv',
    scrollThreshold = 0.8,
    style = { overflow: 'visible' },
    emptyStateMessage,
    isLoading,
}: InfiniteScrollListProps<T>) => {
    const { t } = useTranslation();

    const selectedSet = React.useMemo(
        () => new Set(selectedItems.map(itemIdentifier)),
        [selectedItems, itemIdentifier]
    );

    return (
        <Box
            id={scrollableTarget}
            style={{ maxHeight: '200px', overflow: 'auto', marginTop: '15px' }}
            sx={{
                '& .MuiMenuItem-root': {
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                },
                '&::-webkit-scrollbar': {
                    width: '2px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'common.grey2',
                    borderRadius: '4px',
                },
            }}
        >
            {items.length > 0 ? (
                <InfiniteScroll
                    dataLength={items.length}
                    next={fetchMoreData}
                    hasMore={hasMore}
                    loader={<CircularProgress />}
                    scrollableTarget={scrollableTarget}
                    scrollThreshold={scrollThreshold}
                    style={style}
                >
                    <List sx={{ width: '100%', bgcolor: 'background.paper', minHeight: '140px' }}>
                        {items.map((item, index) => (
                            <ListItem
                                key={`${itemIdentifier(item)} ${index}`}
                                divider
                                sx={{
                                    height: '41px',
                                    py: 0,
                                    px: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: 'none',
                                    '& .MuiTypography-root': {
                                        fontSize: '14px',
                                    },
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedSet.has(itemIdentifier(item))}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => handleItemChange(e, item)}
                                            sx={{
                                                color: 'common.grey1',
                                                '&.Mui-checked': { color: 'common.primary' },
                                            }}
                                        />
                                    }
                                    label={renderLabel(item)}
                                />
                            </ListItem>
                        ))}
                    </List>
                </InfiniteScroll>
            ) : (
                <DataLoader
                    isLoading={isLoading}
                    showNoData
                    noDataMessage={
                        <Typography sx={{ fontSize: '14px', color: 'common.grey1' }}>
                            {emptyStateMessage ? emptyStateMessage : t('no-data-available-by-criteria')}
                        </Typography>
                    }
                    sx={{
                        position: 'relative',
                        height: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                    }}
                />
            )}
        </Box>
    );
};

export default InfiniteScrollList;
